import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CHECKLIST_RECURRENCE_LABELS,
  PLAYBOOK_CATEGORY_LABELS,
  trackOperationsPlaybookEvent,
} from "../../../constants/automapOperationsPlaybookConstants";
import {
  fetchPlaybookChecklist,
  fetchPlaybookChecklists,
  updatePlaybookChecklistItem,
} from "../../../services/automapOperationsPlaybookApi";
import { PlaybookShell } from "./AdminPlaybookShared";

function ChecklistDetail({ checklistId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchPlaybookChecklist(checklistId);
      setData(payload);
      trackOperationsPlaybookEvent("operations_playbook_checklist_viewed", { checklistId });
    } catch (err) {
      setError(err.message || "Error al cargar checklist.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [checklistId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleItem = async (itemId, complete) => {
    setSaving(itemId);
    try {
      const result = await updatePlaybookChecklistItem(checklistId, itemId, { complete });
      setData((prev) => ({ ...prev, progress: result.progress }));
    } catch (err) {
      setError(err.message || "No se pudo guardar.");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <p className="dealer-state-box">Cargando checklist…</p>;
  if (error) return <p className="dealer-state-box">{error}</p>;
  if (!data?.checklist) return null;

  const { checklist, progress } = data;

  return (
    <section className="dealer-state-box">
      <h3>{checklist.title}</h3>
      <p className="muted">{checklist.description}</p>
      <p>
        Progreso: {progress.completed}/{progress.total} ({progress.percent}%)
      </p>
      <ul className="admin-playbook__checklist-items">
        {progress.items.map((item) => (
          <li key={item.id} className={item.complete ? "is-complete" : undefined}>
            <label>
              <input
                type="checkbox"
                checked={item.complete}
                disabled={saving === item.id}
                onChange={(e) => toggleItem(item.id, e.target.checked)}
              />
              {item.label}
            </label>
            {item.route ? (
              <Link to={item.route} className="admin-playbook__checklist-link">
                Abrir →
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
      {checklist.relatedRoutes?.length ? (
        <div className="admin-playbook__route-grid" style={{ marginTop: "0.75rem" }}>
          {checklist.relatedRoutes.map((r) => (
            <Link key={r.href} to={r.href} className="dealer-portal-btn">
              {r.label}
            </Link>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function AdminPlaybookChecklistsPage() {
  const [searchParams] = useSearchParams();
  const selectedId = searchParams.get("checklist");
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPlaybookChecklists()
      .then((data) => setChecklists(data.checklists || []))
      .catch((err) => setError(err.message || "Error al cargar checklists."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PlaybookShell
      active="checklists"
      title="Operational Checklists"
      description="Checklists reutilizables con progreso persistente (mock JSON)."
    >
      {loading ? <p className="dealer-state-box">Cargando…</p> : null}
      {error ? <p className="dealer-state-box">{error}</p> : null}

      {!loading && checklists.length ? (
        <section className="dealer-state-box">
          <h3>Todas las checklists</h3>
          <ul>
            {checklists.map((c) => (
              <li key={c.id}>
                <Link to={`/admin/playbook/checklists?checklist=${c.id}`}>{c.title}</Link>
                <span className="muted">
                  {" "}
                  · {PLAYBOOK_CATEGORY_LABELS[c.category] || c.category} ·{" "}
                  {CHECKLIST_RECURRENCE_LABELS[c.recurrence] || c.recurrence} ·{" "}
                  {c.progress?.percent ?? 0}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {selectedId ? <ChecklistDetail checklistId={selectedId} /> : null}
    </PlaybookShell>
  );
}
